'use strict'

const usageFields = [
  'inputTokens',
  'outputTokens',
  'cacheReadTokens',
  'cacheWriteTokens',
  'reasoningTokens'
]

const virtualInferenceProviderIds = Object.freeze(['host-codex', 'host-claude-p', 'host-xai'])

const asUsageNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? Math.max(0, number) : 0
}

const normalizeUsage = usage => {
  const normalized = {}
  for (const field of usageFields) {
    normalized[field] = asUsageNumber(usage && usage[field])
  }
  return normalized
}

const normalizePrice = (price, divisor = 1) => {
  price = price || {}
  return {
    input: asUsageNumber(price.input) / divisor,
    output: asUsageNumber(price.output) / divisor,
    cached: asUsageNumber(price.cached !== undefined ? price.cached : price.cache_read) / divisor,
    cacheWrite: asUsageNumber(price.cacheWrite !== undefined ? price.cacheWrite : price.cache_write) / divisor
  }
}

const usageTokenBreakdown = usage => {
  const normalized = normalizeUsage(usage)
  const cacheTokens = normalized.cacheReadTokens + normalized.cacheWriteTokens
  const cacheTokensIncludedInInput = normalized.inputTokens >= cacheTokens
  return {
    ...normalized,
    cacheTokensIncludedInInput,
    contextInputTokens: cacheTokensIncludedInInput
      ? normalized.inputTokens
      : normalized.inputTokens + cacheTokens,
    uncachedInputTokens: cacheTokensIncludedInInput
      ? normalized.inputTokens - cacheTokens
      : normalized.inputTokens
  }
}

const usageCostBreakdown = (usage, price, priceDivisor = 1) => {
  const tokens = usageTokenBreakdown(usage)
  const normalizedPrice = normalizePrice(price, priceDivisor)
  return {
    ...tokens,
    uncachedInputDollars: tokens.uncachedInputTokens * normalizedPrice.input,
    cacheReadDollars: tokens.cacheReadTokens * normalizedPrice.cached,
    cacheWriteDollars: tokens.cacheWriteTokens * normalizedPrice.cacheWrite,
    outputDollars: tokens.outputTokens * normalizedPrice.output
  }
}

const normalizeInferenceProviderId = value => String(value || '').trim().toLowerCase()

const billingModelId = (inferenceProviderId, modelId) => {
  inferenceProviderId = normalizeInferenceProviderId(inferenceProviderId)
  modelId = String(modelId || '').trim()
  return inferenceProviderId && modelId ? `${inferenceProviderId}:${modelId}` : undefined
}

const splitBillingModelId = value => {
  const id = String(value || '')
  const separator = id.indexOf(':')
  if (separator < 1 || separator === id.length - 1) return null
  return {
    billingModelId: id,
    inferenceProviderId: normalizeInferenceProviderId(id.slice(0, separator)),
    modelId: id.slice(separator + 1)
  }
}

const isVirtualInferenceProvider = id => virtualInferenceProviderIds.includes(normalizeInferenceProviderId(id))

const isCreditExemptRoute = route => {
  route = route || {}
  const modelId = String(route.modelId || route.model || '')
  const billing = splitBillingModelId(route.billingModelId)
  const providerIds = [
    billing && billing.inferenceProviderId,
    route.routeInferenceProviderId,
    route.inferenceProviderId,
    route.actualInferenceProviderId
  ].map(normalizeInferenceProviderId).filter(Boolean)
  return modelId.startsWith('custom:') ||
    providerIds.includes('custom') ||
    providerIds.some(isVirtualInferenceProvider)
}

module.exports = {
  billingModelId,
  isCreditExemptRoute,
  isVirtualInferenceProvider,
  normalizeInferenceProviderId,
  normalizePrice,
  normalizeUsage,
  splitBillingModelId,
  usageCostBreakdown,
  usageFields,
  usageTokenBreakdown,
  virtualInferenceProviderIds
}
